import { contextOf, heightFor, ModuleSplitSpec, sliceModule } from "./moduleSplitContract";
import { hasCommentSection } from "./slide-kinds";
import { HEADER_HEIGHT, FOOTER_HEIGHT_WITH_COMMENT_BAR, FOOTER_HEIGHT_PLAIN, MODULE_GAP, PAGE_PADDING } from "@/config/layout";

export interface SplitPage {
    slideId: string;
    pageIndex: number;
    totalPagesForSlide: number;
    title: string;
    modules: any[];
    isOverflowPage: boolean;
}

export function footerHeightFor(slideId: string | undefined | null): number {
    return hasCommentSection(slideId)
        ? FOOTER_HEIGHT_WITH_COMMENT_BAR
        : FOOTER_HEIGHT_PLAIN;
}

const UNSPLITTABLE_SLIDES = ["intro", "feedback", "welcome"];

function sectionRootOf(spec: ModuleSplitSpec, index: number): number {
    const chain = contextOf(spec, index);
    return chain.length > 0 ? chain[0] : index;
}

function sectionEndOf(spec: ModuleSplitSpec, index: number): number {
    const root = sectionRootOf(spec, index);
    let end = index + 1;
    while (end < spec.items.length && sectionRootOf(spec, end) === root) end++;
    return end;
}

function keepSectionsTogether(
    spec: ModuleSplitSpec,
    start: number,
    end: number,
    hasModulesAbove: boolean,
    availableHeight: number,
): number {
    if (!spec.parentOf || end <= start || end >= spec.items.length) return end;

    const root = sectionRootOf(spec, end);
    if (root === end) return end;
    if (root > start) return root;

    if (
        root === start &&
        hasModulesAbove &&
        heightFor(spec, start, sectionEndOf(spec, start)) <= availableHeight
    ) {
        return start;
    }

    let cut = end;
    while (cut - 1 > start && spec.parentOf[cut] === cut - 1) cut--;
    return cut;
}

export function splitPagesForViewport(
    pages: any[],
    viewportHeight: number,
    moduleHeights: Map<string, number>,
    moduleSpecs: Map<string, ModuleSplitSpec> = new Map(),
): SplitPage[] {
    const result: SplitPage[] = [];

    for (const page of pages) {
        const slideId = page.slideId || "";
        const title = page.title || "";
        const modules: any[] = page.modules || [];

        const footerHeight = footerHeightFor(slideId);
        const availableHeight =
            viewportHeight - HEADER_HEIGHT - footerHeight - PAGE_PADDING;

        if (UNSPLITTABLE_SLIDES.includes(slideId) || modules.length === 0) {
            result.push({
                slideId,
                pageIndex: 0,
                totalPagesForSlide: 1,
                title,
                modules,
                isOverflowPage: false,
            });
            continue;
        }

        const splitForSlide: SplitPage[] = [];
        let currentModules: any[] = [];
        let currentHeight = 0;

        const flush = () => {
            if (currentModules.length === 0) return;
            const pageIndex = splitForSlide.length;
            splitForSlide.push({
                slideId,
                pageIndex,
                totalPagesForSlide: 0,
                title,
                modules: currentModules,
                isOverflowPage: pageIndex > 0,
            });
            currentModules = [];
            currentHeight = 0;
        };

        for (const mod of modules) {
            const spec = mod?.id ? moduleSpecs.get(mod.id) : undefined;

            if (!spec || spec.items.length === 0) {
                const modHeight = moduleHeights.get(mod.id) || 150;
                const gapHeight = currentModules.length > 0 ? MODULE_GAP : 0;
                if (
                    currentHeight + gapHeight + modHeight <= availableHeight ||
                    currentModules.length === 0
                ) {
                    currentModules.push(mod);
                    currentHeight += gapHeight + modHeight;
                } else {
                    flush();
                    currentModules.push(mod);
                    currentHeight = modHeight;
                }
                continue;
            }

            let start = 0;
            while (start < spec.items.length) {
                const gapHeight = currentModules.length > 0 ? MODULE_GAP : 0;
                let end = start;

                while (end < spec.items.length) {
                    const candidate = end + 1;
                    const needed = heightFor(spec, start, candidate);
                    const isLoneFirstItem =
                        currentModules.length === 0 && candidate === start + 1;
                    if (
                        currentHeight + gapHeight + needed > availableHeight &&
                        !isLoneFirstItem
                    ) {
                        break;
                    }
                    end = candidate;
                }

                end = keepSectionsTogether(
                    spec,
                    start,
                    end,
                    currentModules.length > 0,
                    availableHeight,
                );

                if (end === start) {
                    flush();
                    continue;
                }

                currentModules.push(sliceModule(mod, start, end));
                currentHeight += gapHeight + heightFor(spec, start, end);
                start = end;
                if (start < spec.items.length) flush();
            }
        }

        flush();

        const total = splitForSlide.length;
        for (const sp of splitForSlide) {
            sp.totalPagesForSlide = total;
            result.push(sp);
        }
    }

    return result;
}
